import type { Instant, UserId } from './types'

/**
 * Shifts: when an hourly person was at work, and nothing else.
 *
 * **A shift is one clock-in and one clock-out.** It does not say what the
 * person did, and nothing here will ever ask them to. A timer per task is the
 * version of this that nobody presses: the tool would collect fiction, and the
 * person would learn that Kronos is the thing that nags. What they did is
 * already written down elsewhere — every build step they answer carries their
 * name and an instant — so the report *derives* it (`WorkDone`) rather than
 * asking for it twice.
 *
 * **Kronos records hours; it does not pay them.** No rate, no currency, no
 * overtime rule lives here (design guide §16: accounting is out of scope). The
 * report is a list of hours for whoever does the paying, and it stops there.
 *
 * Minutes are integers, like every other quantity in this package: each shift
 * is rounded to the minute once, and every total is a sum of those — so the
 * rows on the report always add up to the total printed under them. Rounding
 * the total separately is how a payslip ends up a minute off its own detail.
 */

export interface Shift {
  readonly id: string
  readonly userId: UserId
  readonly startedAt: Instant
  /** Null while the person is on shift. */
  readonly endedAt: Instant | null
  /**
   * Set when anyone typed a time in rather than pressing the button — a
   * forgotten clock-out fixed the next morning, or a shift added after the
   * fact. Allowed, because the alternative is a wrong number nobody can
   * correct; shown on the report, because whoever pays should know which
   * times were typed.
   */
  readonly editedAt: Instant | null
}

const MINUTE = 60_000
const HOUR = 60 * MINUTE

/**
 * An open shift older than this was almost certainly not ended by its owner
 * going home on time. Long enough for a genuinely long day; short enough that
 * yesterday morning's clock-in is caught before lunch today.
 */
export const FORGOTTEN_AFTER_HOURS = 14

/** No single shift is longer than a day. Typing 17:00 the next day is a typo. */
export const LONGEST_SHIFT_HOURS = 24

/** Whole minutes on shift; an open shift counts up to `now`. Never negative. */
export function shiftMinutes(shift: Pick<Shift, 'startedAt' | 'endedAt'>, now: Instant): number {
  const end = Date.parse(shift.endedAt ?? now)
  return Math.max(0, Math.round((end - Date.parse(shift.startedAt)) / MINUTE))
}

/** Still open, and open for longer than any real shift. */
export function isForgotten(shift: Pick<Shift, 'startedAt' | 'endedAt'>, now: Instant): boolean {
  return (
    shift.endedAt === null &&
    Date.parse(now) - Date.parse(shift.startedAt) > FORGOTTEN_AFTER_HOURS * HOUR
  )
}

/** The person's open shift, if they have one. The schema allows at most one. */
export function openShift<S extends Pick<Shift, 'endedAt'>>(shifts: readonly S[]): S | null {
  return shifts.find((s) => s.endedAt === null) ?? null
}

// --- Editing ----------------------------------------------------------------

export type ShiftTimesRefusal = 'ends_before_it_starts' | 'starts_in_the_future' | 'too_long' | 'overlaps'

export const SHIFT_REFUSAL_MESSAGE: Readonly<Record<ShiftTimesRefusal, string>> = {
  ends_before_it_starts: 'A shift has to end after it starts.',
  starts_in_the_future: 'That start time has not happened yet.',
  too_long: `A shift can be at most ${LONGEST_SHIFT_HOURS} hours. Check the date on the end time.`,
  overlaps: 'Those times overlap another shift.',
}

/**
 * Settle a typed-in pair of times, or say why they cannot stand.
 *
 * An end time in the future is **clamped to now** rather than refused: "I left
 * at 4:30" typed at 4:20 means "I am leaving", and refusing it makes someone
 * wait ten minutes to go home. A *start* in the future has no such reading.
 *
 * `others` is the same person's other shifts. An open one runs to `now`, so a
 * typed shift cannot be slipped underneath the one in progress.
 */
export function settleShiftTimes(
  candidate: { readonly startedAt: Instant; readonly endedAt: Instant | null },
  others: readonly Pick<Shift, 'id' | 'startedAt' | 'endedAt'>[],
  now: Instant,
):
  | { readonly ok: true; readonly startedAt: Instant; readonly endedAt: Instant | null }
  | { readonly ok: false; readonly reason: ShiftTimesRefusal } {
  const start = Date.parse(candidate.startedAt)
  const nowMs = Date.parse(now)
  if (start > nowMs) return { ok: false, reason: 'starts_in_the_future' }

  const endMs = candidate.endedAt === null ? null : Math.min(Date.parse(candidate.endedAt), nowMs)
  if (endMs !== null && endMs <= start) return { ok: false, reason: 'ends_before_it_starts' }
  if ((endMs ?? nowMs) - start > LONGEST_SHIFT_HOURS * HOUR) return { ok: false, reason: 'too_long' }

  const thisEnd = endMs ?? nowMs
  for (const other of others) {
    const oStart = Date.parse(other.startedAt)
    const oEnd = other.endedAt === null ? nowMs : Date.parse(other.endedAt)
    // Touching is fine — out for lunch at 12:00, back in at 12:00.
    if (start < oEnd && oStart < thisEnd) return { ok: false, reason: 'overlaps' }
  }

  return {
    ok: true,
    startedAt: new Date(start).toISOString() as Instant,
    endedAt: endMs === null ? null : (new Date(endMs).toISOString() as Instant),
  }
}

// --- Local dates ------------------------------------------------------------
//
// A shift belongs to a *day*, and a day only exists in a time zone: 23:30 in
// Vancouver is already tomorrow in UTC. The zone is a parameter everywhere, so
// the phone and the December payroll question get the same answer.

/** `YYYY-MM-DD`, a calendar date with no zone attached. */
export type LocalDate = string & { readonly __brand: 'LocalDate' }

function partsIn(instant: number, timeZone: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant))
  const out: Record<string, number> = {}
  for (const p of parts) if (p.type !== 'literal') out[p.type] = Number(p.value)
  return out
}

const pad = (n: number): string => String(n).padStart(2, '0')

export function localDateOf(instant: Instant, timeZone: string): LocalDate {
  const p = partsIn(Date.parse(instant), timeZone)
  return `${p.year}-${pad(p.month ?? 0)}-${pad(p.day ?? 0)}` as LocalDate
}

/** `08:02` — 24-hour, because a payroll sheet must not be read as pm by mistake. */
export function localClockOf(instant: Instant, timeZone: string): string {
  const p = partsIn(Date.parse(instant), timeZone)
  return `${pad((p.hour ?? 0) % 24)}:${pad(p.minute ?? 0)}`
}

function ymd(date: LocalDate): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number)
  return [y ?? 0, m ?? 0, d ?? 0]
}

/**
 * The instant a local date begins. Computed by asking the zone what its
 * offset is, twice — once is wrong on the morning a daylight-saving change
 * happens overnight.
 */
export function startOfLocalDate(date: LocalDate, timeZone: string): Instant {
  const [y, m, d] = ymd(date)
  const guess = Date.UTC(y, m - 1, d)
  const offset = (t: number): number => {
    const p = partsIn(t, timeZone)
    return (
      Date.UTC(p.year ?? 0, (p.month ?? 1) - 1, p.day ?? 1, (p.hour ?? 0) % 24, p.minute ?? 0, p.second ?? 0) - t
    )
  }
  const first = guess - offset(guess)
  return new Date(guess - offset(first)).toISOString() as Instant
}

export function addDays(date: LocalDate, days: number): LocalDate {
  const [y, m, d] = ymd(date)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}` as LocalDate
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `Fri 16 Oct`. */
export function dayLabel(date: LocalDate): string {
  const [y, m, d] = ymd(date)
  return `${WEEKDAY[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTH[m - 1]}`
}

// --- Pay periods ------------------------------------------------------------

/**
 * Twice a month: the 1st to the 15th, and the 16th to the end of the month.
 *
 * Inclusive local dates at both ends. This is the one assumption in the file
 * that belongs to whoever pays rather than to the product; if they pay weekly,
 * these three functions are what changes and nothing that calls them does.
 */
export interface PayPeriod {
  readonly from: LocalDate
  readonly to: LocalDate
}

export function payPeriodContaining(date: LocalDate): PayPeriod {
  const [y, m, d] = ymd(date)
  const month = `${y}-${pad(m)}`
  if (d <= 15) return { from: `${month}-01` as LocalDate, to: `${month}-15` as LocalDate }
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { from: `${month}-16` as LocalDate, to: `${month}-${pad(last)}` as LocalDate }
}

export function previousPayPeriod(period: PayPeriod): PayPeriod {
  return payPeriodContaining(addDays(period.from, -1))
}

export function nextPayPeriod(period: PayPeriod): PayPeriod {
  return payPeriodContaining(addDays(period.to, 1))
}

/** `[start, end)` as instants: midnight on `from` to midnight after `to`. */
export function payPeriodBounds(
  period: PayPeriod,
  timeZone: string,
): { readonly start: Instant; readonly end: Instant } {
  return {
    start: startOfLocalDate(period.from, timeZone),
    end: startOfLocalDate(addDays(period.to, 1), timeZone),
  }
}

/** `16 – 31 Oct 2026`, or `26 Dec 2026 – 8 Jan 2027` across a year. */
export function periodLabel(period: PayPeriod): string {
  const [fy, fm, fd] = ymd(period.from)
  const [ty, tm, td] = ymd(period.to)
  if (fy === ty && fm === tm) return `${fd} – ${td} ${MONTH[tm - 1]} ${ty}`
  if (fy === ty) return `${fd} ${MONTH[fm - 1]} – ${td} ${MONTH[tm - 1]} ${ty}`
  return `${fd} ${MONTH[fm - 1]} ${fy} – ${td} ${MONTH[tm - 1]} ${ty}`
}

// --- Formatting -------------------------------------------------------------

/** `7h 50m`. The one format hours are shown in, on screen and on paper. */
export function formatMinutes(minutes: number): string {
  const m = Math.max(0, Math.round(minutes))
  return `${Math.floor(m / 60)}h ${pad(m % 60)}m`
}

/** `7.83` — decimal hours, which is what a payroll system wants typed in. */
export function decimalHours(minutes: number): string {
  return (Math.max(0, minutes) / 60).toFixed(2)
}

// --- The report -------------------------------------------------------------

/**
 * Something a person finished, as the execution engine recorded it: an arm
 * built, an inspection signed off. `label` is what it was ("Arm build").
 */
export interface WorkDone {
  readonly userId: UserId
  readonly finishedAt: Instant
  readonly label: string
}

export interface ShiftLine {
  readonly id: string
  readonly startedAt: Instant
  readonly endedAt: Instant | null
  /** Zero for a forgotten shift: it is not counted until it is fixed. */
  readonly minutes: number
  /** Open right now, and not yet suspicious. */
  readonly onShift: boolean
  /** Open far too long — see `FORGOTTEN_AFTER_HOURS`. */
  readonly forgotten: boolean
  readonly edited: boolean
}

export interface DayLine {
  readonly date: LocalDate
  readonly shifts: readonly ShiftLine[]
  readonly minutes: number
  /** "3 × Arm build", most first. */
  readonly work: readonly { readonly label: string; readonly count: number }[]
  /** Work was signed off on a day with no shift — a clock-in that never happened. */
  readonly workWithoutShift: boolean
}

export interface PersonHours {
  readonly userId: UserId
  readonly name: string
  readonly email: string
  readonly minutes: number
  readonly days: readonly DayLine[]
  /** Forgotten clock-outs plus days of work with no shift. What to look at first. */
  readonly needsALook: number
}

export interface HoursReport {
  readonly period: PayPeriod
  readonly timeZone: string
  readonly minutes: number
  readonly people: readonly PersonHours[]
  readonly needsALook: number
}

/**
 * Everything whoever pays needs for one period, one person after another.
 *
 * A shift belongs to the local date it **started** on. A shift that runs past
 * midnight is one shift, not two halves — splitting it would put a line on the
 * report for a day the person never clocked in on.
 *
 * Everyone in `people` appears, even with no hours: a contractor who worked
 * nothing this period is a fact worth printing, and silently leaving them off
 * looks exactly like a bug.
 */
export function hoursReport(input: {
  readonly period: PayPeriod
  readonly timeZone: string
  readonly now: Instant
  readonly people: readonly { readonly id: UserId; readonly name: string; readonly email: string }[]
  readonly shifts: readonly Shift[]
  readonly work: readonly WorkDone[]
}): HoursReport {
  const { period, timeZone, now } = input
  const inPeriod = (date: LocalDate): boolean => date >= period.from && date <= period.to

  const people = input.people.map((person): PersonHours => {
    const byDay = new Map<LocalDate, { shifts: ShiftLine[]; work: Map<string, number> }>()
    const day = (date: LocalDate): { shifts: ShiftLine[]; work: Map<string, number> } => {
      let entry = byDay.get(date)
      if (!entry) {
        entry = { shifts: [], work: new Map() }
        byDay.set(date, entry)
      }
      return entry
    }

    for (const s of input.shifts) {
      if (s.userId !== person.id) continue
      const date = localDateOf(s.startedAt, timeZone)
      if (!inPeriod(date)) continue
      const forgotten = isForgotten(s, now)
      day(date).shifts.push({
        id: s.id,
        startedAt: s.startedAt,
        endedAt: s.endedAt,
        // A forgotten shift counts **nothing** until somebody types the real
        // end. Running it to now would put a 120-hour Friday on a payslip;
        // guessing a length would put a number on paper nobody chose.
        minutes: forgotten ? 0 : shiftMinutes(s, now),
        onShift: s.endedAt === null && !forgotten,
        forgotten,
        edited: s.editedAt !== null,
      })
    }
    for (const w of input.work) {
      if (w.userId !== person.id) continue
      const date = localDateOf(w.finishedAt, timeZone)
      if (!inPeriod(date)) continue
      const work = day(date).work
      work.set(w.label, (work.get(w.label) ?? 0) + 1)
    }

    const days = [...byDay.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([date, entry]): DayLine => {
        const shifts = [...entry.shifts].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt))
        return {
          date,
          shifts,
          minutes: shifts.reduce((sum, s) => sum + s.minutes, 0),
          work: [...entry.work.entries()]
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
          workWithoutShift: shifts.length === 0 && entry.work.size > 0,
        }
      })

    return {
      userId: person.id,
      name: person.name,
      email: person.email,
      minutes: days.reduce((sum, d) => sum + d.minutes, 0),
      days,
      needsALook:
        days.reduce((n, d) => n + d.shifts.filter((s) => s.forgotten).length, 0) +
        days.filter((d) => d.workWithoutShift).length,
    }
  })

  return {
    period,
    timeZone,
    minutes: people.reduce((sum, p) => sum + p.minutes, 0),
    people,
    needsALook: people.reduce((n, p) => n + p.needsALook, 0),
  }
}

// --- History ----------------------------------------------------------------

export interface PeriodSummary {
  readonly period: PayPeriod
  readonly minutes: number
  /** Distinct local dates with a counted shift. */
  readonly days: number
  readonly shifts: number
  /** Forgotten clock-outs still waiting for a time — counted as nothing. */
  readonly unfixed: number
}

/**
 * Every pay period a person has worked in, newest first — the "what have I
 * done" list. The same arithmetic as `hoursReport`: a shift belongs to the
 * period of the local date it started on, a forgotten one counts nothing, and
 * the total is a sum of whole-minute shifts so it matches the period's own
 * page to the minute.
 */
export function periodHistory(shifts: readonly Shift[], timeZone: string, now: Instant): readonly PeriodSummary[] {
  const byPeriod = new Map<LocalDate, { period: PayPeriod; minutes: number; dates: Set<LocalDate>; shifts: number; unfixed: number }>()
  for (const s of shifts) {
    const date = localDateOf(s.startedAt, timeZone)
    const period = payPeriodContaining(date)
    let entry = byPeriod.get(period.from)
    if (!entry) {
      entry = { period, minutes: 0, dates: new Set(), shifts: 0, unfixed: 0 }
      byPeriod.set(period.from, entry)
    }
    entry.shifts += 1
    if (isForgotten(s, now)) {
      entry.unfixed += 1
      continue
    }
    const minutes = shiftMinutes(s, now)
    entry.minutes += minutes
    if (minutes > 0) entry.dates.add(date)
  }
  return [...byPeriod.values()]
    .sort((a, b) => (a.period.from < b.period.from ? 1 : -1))
    .map((e) => ({ period: e.period, minutes: e.minutes, days: e.dates.size, shifts: e.shifts, unfixed: e.unfixed }))
}
